import React, { useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { FilePlus2, Paperclip } from "lucide-react";
import { apiBase, dateLabel, dueLabel, reviewApi, ReviewApiError } from "./api";

export type DocumentRequest = {
  id: string;
  note: string;
  created_at: string;
  fulfilled_at: string | null;
  files: { id: string; name: string; contentType: string; addedAt: string }[];
};
const ACCEPT = ".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp,.gif";

async function upload(reviewId: string, requestId: string, files: File[]) {
  const form = new FormData();
  files.forEach(f => form.append("files", f));
  const response = await fetch(
    `${apiBase}/api/reviews/${reviewId}/document-requests/${requestId}/files`,
    { method: "POST", credentials: "include", body: form }
  );
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new ReviewApiError(
      response.status,
      result.message || "The upload did not complete"
    );
  return result;
}

/**
 * Documents the attorney asks for during a review. The attorney requests one
 * in Messages; the client uploads against that request, and the files join the
 * client's materials as added after submission.
 */
export default function DocumentRequests({
  reviewId,
  requests,
  attorney,
  canRequest,
  canUpload,
  onChanged,
  onError,
  onReminder,
  onViewTerms,
  phase = "legacy",
}: {
  reviewId: string;
  requests: DocumentRequest[];
  attorney: boolean;
  /** The attorney may ask for documents while the review and follow-up are open. */
  canRequest: boolean;
  canUpload: boolean;
  onChanged: () => Promise<void>;
  onError: (e: any) => void;
  /** Opens a reminder draft, without sending it or replacing an unsent message. */
  onReminder?: (request: DocumentRequest) => void;
  onViewTerms?: () => void;
  phase?: "proposal" | "agreed" | "legacy";
}) {
  const [asking, setAsking] = useState(false),
    [note, setNote] = useState(""),
    [busy, setBusy] = useState(false),
    [chosen, setChosen] = useState<Record<string, File[]>>({}),
    [error, setError] = useState("");
  const requestKey = useRef(crypto.randomUUID());
  async function ask() {
    setBusy(true);
    setError("");
    try {
      await reviewApi(`/${reviewId}/document-requests`, "POST", {
        note: note.trim(),
        requestKey: requestKey.current,
      });
      requestKey.current = crypto.randomUUID();
      setNote("");
      setAsking(false);
      await onChanged();
    } catch (e: any) {
      setError(e.message);
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  async function send(request: DocumentRequest) {
    const files = chosen[request.id] || [];
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      await upload(reviewId, request.id, files);
      setChosen(old => ({ ...old, [request.id]: [] }));
      await onChanged();
    } catch (e: any) {
      setError(e.message);
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  const open = requests.filter(r => !r.fulfilled_at);
  const guidance = attorney
    ? phase === "agreed"
      ? "Ask only for documents needed for the agreed question. Requests do not change the scope, fee, or deadline. Additional work needs a new review."
      : phase === "proposal"
        ? "Request documents to clarify the proposed question. If more work is needed, revise the proposal and obtain client approval before starting."
        : "Request documents needed for the existing question. Discuss any additional work with the client before proceeding."
    : phase === "agreed"
      ? "Upload only the documents requested for this question. Sending them does not change the agreed scope, fee, or deadline."
      : phase === "proposal"
        ? "These documents help your attorney clarify the proposal. Sending them does not approve new terms or start the review."
        : "Upload only the documents requested for this question. Discuss any additional work with your attorney before proceeding.";
  const scopeNote = (
    <div className="document-request-scope">
      <p className="mb-1">{guidance}</p>
      {onViewTerms && (
        <Button variant="link" size="sm" className="p-0" onClick={onViewTerms}>
          View review terms
        </Button>
      )}
    </div>
  );
  if (!attorney && !requests.length) return null;
  return (
    <section className="document-requests" aria-label="Document requests">
      {error && <Alert variant="danger">{error}</Alert>}
      {!asking &&
        open.length > 0 &&
        (attorney ? canRequest : canUpload) &&
        scopeNote}
      {attorney ? (
        <>
          {open.map(r => (
            <div key={r.id} className="document-request-status">
              <p>
                <Paperclip size={12} />{" "}
                {canUpload
                  ? "Waiting for the client to send:"
                  : "Not sent before this review ended:"}{" "}
                <strong>{r.note}</strong>
              </p>
              <small>Requested {dueLabel(r.created_at)}</small>
              {onReminder && canRequest && (
                <Button variant="link" size="sm" onClick={() => onReminder(r)}>
                  Write a reminder
                </Button>
              )}
            </div>
          ))}
          {asking && canRequest ? (
            <Form
              onSubmit={e => {
                e.preventDefault();
                if (note.trim()) void ask();
              }}
            >
              <Form.Group controlId={`document-request-${reviewId}`}>
                <Form.Label>What document do you need?</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={2000}
                  value={note}
                  disabled={busy}
                  placeholder="For example: the signed contract, including the termination and payment clauses."
                  onChange={e => {
                    setNote(e.target.value);
                    requestKey.current = crypto.randomUUID();
                  }}
                />
              </Form.Group>
              {scopeNote}
              <div className="document-request-actions">
                <Button
                  variant="light"
                  size="sm"
                  disabled={busy}
                  onClick={() => setAsking(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={busy || !note.trim()}>
                  {busy ? "Sending…" : "Send request"}
                </Button>
              </div>
            </Form>
          ) : (
            canRequest && (
              <Button
                variant="outline-dark"
                size="sm"
                onClick={() => setAsking(true)}
              >
                <FilePlus2 size={13} /> Request a document
              </Button>
            )
          )}
        </>
      ) : (
        requests.map(r =>
          r.fulfilled_at ? (
            <p key={r.id} className="document-request-status">
              <Paperclip size={12} /> Sent for “{r.note}”:{" "}
              {r.files.map(f => f.name).join(", ")} ·{" "}
              {dateLabel(r.fulfilled_at)}
            </p>
          ) : (
            <div key={r.id} className="document-request-card">
              <strong>Your attorney asked for a document</strong>
              <p>{r.note}</p>
              {canUpload ? (
                <div className="document-request-actions">
                  <Form.Control
                    type="file"
                    size="sm"
                    multiple
                    accept={ACCEPT}
                    aria-label={`Files for: ${r.note}`}
                    disabled={busy}
                    onChange={e => {
                      const input = e.target as HTMLInputElement;
                      setChosen(old => ({
                        ...old,
                        [r.id]: Array.from(input.files || []),
                      }));
                    }}
                  />
                  <Button
                    size="sm"
                    disabled={busy || !(chosen[r.id] || []).length}
                    onClick={() => send(r)}
                  >
                    {busy ? "Uploading…" : "Upload"}
                  </Button>
                </div>
              ) : (
                <small>
                  This review has ended, so files can no longer be sent.
                </small>
              )}
              {canUpload && (
                <small>
                  PDF, Word, text, or image files, up to 25 MB each.
                </small>
              )}
            </div>
          )
        )
      )}
    </section>
  );
}
